import { MouseEvent, useEffect, useState } from 'react';

import { trace } from '../../../../../configuration/setup/trace';
import { FormStepSelectBrand } from './FormStepSelectBrand';
import { FormStepConnector } from './FormStepConnector';
import { FormStepSelectTelematics } from './FormStepSelectTelematics';
import { DialogFooter } from './DialogFooter';
import { SimpleDialogWrapper } from './SimpleDialogWrapper';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { ALL_BRAND_TILES, BrandTile } from './configuration/brandTileConfig';
import { TELEMATICS_TILES, TelematicsTile } from './configuration/telematicsTileConfig';
import { FormStepSelectAssetType } from './FormStepSelectAssetType';
import { ASSET_TYPE_TILES, AssetTypeTile } from './configuration/assetTypeTileConfig';
import { useSelector } from 'react-redux';
import { getAssets, getEasyOnboardingSelectedAssetId } from '../../../selectors/assets/Assets.selector';
import { getIsEuTenant } from '../../../../../configuration/login/selectors';

export enum FormStep {
    connector = 'connector',
    selectAssetType = 'selectAssetType',
    selectBrand = 'selectBrand',
    selectTelematics = 'selectTelematics',
}

export enum FormStepNavigation {
    prev = 'prev',
    next = 'next',
}

const initialState = {
    formStep: FormStep.selectAssetType,
    selectedAssetType: undefined,
};
const EasyOnboardingDialog = () => {
    const [formStep, setFormStep] = useState<FormStep>(initialState.formStep);
    const [historyFormSteps, setHistoryFormSteps] = useState<FormStep[]>([initialState.formStep]);
    const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeTile | undefined>(
        initialState.selectedAssetType
    );
    const [selectedBrand, setSelectedBrand] = useState<BrandTile | undefined>(undefined);
    const [selectedTelematics, setSelectedTelematic] = useState<TelematicsTile | undefined>(undefined);
    const selectedAssetId = useSelector(getEasyOnboardingSelectedAssetId);
    const assets = useSelector(getAssets);
    const isEuTenant = useSelector(getIsEuTenant);

    const showDialog = useAppSelector((state) => state.assets.easyOnboardingDialog.show);
    useEffect(() => {
        if (!showDialog && formStep != initialState.formStep) {
            setFormStep(initialState.formStep);
            setSelectedBrand(undefined);
            setSelectedTelematic(undefined);
        }
    }, [showDialog, formStep]);

    useEffect(() => {
        if (selectedAssetId !== undefined) {
            const selectedAsset = assets.find((it) => it.id === selectedAssetId);
            const alreadySelectedBrand = ALL_BRAND_TILES.find(
                (it) => it.name.toLowerCase().trim() === selectedAsset?.brand?.toLowerCase()
            );
            trace('selectedAsset\n', selectedAsset);
            if (alreadySelectedBrand) {
                setSelectedBrand(alreadySelectedBrand);
            }
            if (selectedAsset?.type !== undefined) {
                setSelectedAssetType(ASSET_TYPE_TILES[selectedAsset?.type]);
            }
            if (isEuTenant) {
                setFormStep(FormStep.selectTelematics);
            } else {
                // non-eu tenants should not see the brand/telematics selection
                // when adding a telematics device from sidebar
                const alreadySelectedAssetType = selectedAsset?.type
                    ? ASSET_TYPE_TILES[selectedAsset?.type]
                    : undefined;
                setSelectedAssetType(alreadySelectedAssetType);
                if (selectedAsset && selectedAsset.type !== 'trailer') {
                    setFormStep(FormStep.selectTelematics);
                } else {
                    setSelectedTelematic(TELEMATICS_TILES.noTelematics);
                    setFormStep(FormStep.connector);
                }
            }
        }
    }, [assets, selectedAssetId, isEuTenant]);

    useEffect(() => {
        trace(
            '\nformStep',
            formStep,
            '\nhistoryFormSteps',
            historyFormSteps,
            '\nselectedAssetType',
            selectedAssetType,
            '\nselectedBrand',
            selectedBrand,
            '\nselectedTelematicsBrand',
            selectedTelematics
        );
    });

    const handleFormStepNavigation = (goto: FormStepNavigation, e?: MouseEvent) => {
        const currentHistoryFormSteps = historyFormSteps;

        if (goto === FormStepNavigation.prev) {
            setFormStep(historyFormSteps[historyFormSteps.length - 1]);
            currentHistoryFormSteps.pop();
            setHistoryFormSteps(currentHistoryFormSteps);
        } else if (goto === FormStepNavigation.next) {
            if (formStep !== currentHistoryFormSteps[currentHistoryFormSteps.length - 1]) {
                setHistoryFormSteps([...currentHistoryFormSteps, formStep]);
            }
            if (isEuTenant) {
                switch (formStep) {
                    case FormStep.selectAssetType:
                        setFormStep(FormStep.selectBrand);
                        break;
                    case FormStep.selectBrand:
                        setFormStep(FormStep.selectTelematics);
                        break;
                    case FormStep.selectTelematics:
                        setFormStep(FormStep.connector);
                        break;
                    case FormStep.connector:
                        break;
                }
            } else {
                // for tenant != rio.eu jump directly to connector dialog
                switch (formStep) {
                    case FormStep.selectAssetType:
                        // for non-eu tenants, trailers have only the 'no telematics device option'
                        // therefore, go directly to connector screen
                        (e?.target as HTMLImageElement).alt === 'trailer'
                            ? setFormStep(FormStep.connector)
                            : setFormStep(FormStep.selectTelematics);
                        break;
                    case FormStep.selectTelematics:
                        setFormStep(FormStep.connector);
                        break;
                    case FormStep.connector:
                        break;
                }
            }
        }
    };

    const renderDialogBody = () => {
        switch (formStep) {
            case FormStep.selectBrand:
                return (
                    <FormStepSelectBrand
                        selectedAssetType={selectedAssetType}
                        setSelectedBrand={setSelectedBrand}
                        handleFormStepNavigation={handleFormStepNavigation}
                    />
                );
            case FormStep.selectTelematics:
                return (
                    <FormStepSelectTelematics
                        selectedAssetId={selectedAssetId}
                        selectedBrand={selectedBrand}
                        selectedAssetType={selectedAssetType}
                        setSelectedTelematic={setSelectedTelematic}
                        handleFormStepNavigation={handleFormStepNavigation}
                    />
                );
            case FormStep.connector:
                return (
                    <FormStepConnector
                        selectedTelematic={selectedTelematics}
                        selectedAssetType={selectedAssetType}
                        selectedBrand={selectedBrand}
                    />
                );
            default:
            case FormStep.selectAssetType:
                return (
                    <FormStepSelectAssetType
                        setSelectedAssetType={setSelectedAssetType}
                        handleFormStepNavigation={handleFormStepNavigation}
                    />
                );
        }
    };
    const dialogContent = (
        <div>
            {renderDialogBody()}
            <DialogFooter
                formStep={formStep}
                handleFormStepNavigation={handleFormStepNavigation}
                selectedTelematics={selectedTelematics}
                setSelectedBrand={setSelectedBrand}
                selectedBrand={selectedBrand}
                selectedAssetId={selectedAssetId}
            />
        </div>
    );
    return (
        <SimpleDialogWrapper
            formStep={formStep}
            selectedAssetType={selectedAssetType}
            selectedBrand={selectedBrand}
            selectedTelematics={selectedTelematics}
            dialogContent={dialogContent}
        />
    );
};

export default EasyOnboardingDialog;
